import {singleton} from "tsyringe";
import {RequestMethod} from "./request.method.enum";
import {AssetUploadResult} from "./asset-upload-result.type";
import {HttpError} from "./http-error";
import {LockConflictError} from "./lock-conflict.error";

@singleton()
export class BackendClient {

    public async get<T>(uri: string, authRequired = false, requestBody?: Record<string, any>, signal?: AbortSignal): Promise<T | null> {
        return this.request<T>(
            RequestMethod.GET,
            uri,
            requestBody ?? null,
            authRequired,
            signal
        );
    }

    public async post<T>(uri: string, authRequired = false, requestBody: Record<string, any> = {}, signal?: AbortSignal): Promise<T | null> {
        return this.request<T>(
            RequestMethod.POST,
            uri,
            requestBody,
            authRequired,
            signal
        )
    }

    /**
     * Uploads a single binary file to POST /api/v1/files as multipart/form-data.
     *
     * Bypasses request() deliberately: that helper hardcodes a JSON Content-Type and
     * JSON.stringify()s the body, which would strip the multipart boundary and leave
     * the backend with no uploaded file. The Content-Type is therefore never set by
     * hand — fetch derives it (including the boundary) from the FormData body.
     */
    public async uploadFile(file: File, signal?: AbortSignal): Promise<AssetUploadResult> {

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch('/api/v1/files', {
            method: RequestMethod.POST,
            cache: 'no-cache',
            credentials: 'include',
            body: formData,
            signal
        });

        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('o2d.session-expired'));
            throw new Error('Session expired (401)');
        }

        if (~~(response.status / 100) !== 2) {
            throw new Error(
                `upload POST /api/v1/files failed with status ${response.status.toString()} (${response.statusText})`
            );
        }

        return ((await response.json()) as { data: AssetUploadResult }).data;
    }

    protected async request<T>(method: RequestMethod, uri: string, body?: Record<string, any> | null, authRequired = false, signal?: AbortSignal): Promise<T | null> {

        const requestSettings: RequestInit = {
            method,
            cache: `no-cache`,
            redirect: `manual`,
            headers: {
                'Content-Type': 'application/json'
            },
            signal
        }

        if (authRequired) {
            requestSettings.credentials = "include";
        }

        if (body) {
            if (method === RequestMethod.GET) {
                uri += `?` + Object.entries(body).map(([key, value]) => {
                    if (Array.isArray(value)) {
                        return value.map(singleValue => `${key}[]=${singleValue}`).join(`&`)
                    } else {
                        return `${key}=${encodeURIComponent(value)}`
                    }
                }).join(`&`);
            } else {
                requestSettings.body = JSON.stringify(body);
            }
        }

        const request = new Request(uri, requestSettings);
        const response = await fetch(request);

        // Handle 401 Unauthorized — dispatch session-expired event before throwing
        if (response.status === 401) {
            window.dispatchEvent(new CustomEvent('o2d.session-expired'));
            throw new Error('Session expired (401)');
        }

        // 409 = somebody else holds the edit lock and NOTHING was written.
        // Special-cased BEFORE the generic non-2xx throw below, the same shape as
        // the 401 case above it: one status that means something specific. The
        // distinction is the whole point — without it a lost race is
        // indistinguishable from a crashed server, and the client cannot offer
        // the take-over that is the only recovery from an abandoned lock.
        //
        // Only `request()` gets this. `uploadFile()` above posts to
        // /api/v1/files, which is not the command endpoint and cannot answer a
        // lock conflict.
        if (response.status === 409) {
            throw new LockConflictError(
                `request ${method.toUpperCase()} ${uri} failed with status ${response.status.toString()} (${response.statusText})`
            );
        }

        if (~~(response.status / 100) !== 2) {
            // An HttpError, not a bare Error: the status has to reach the caller
            // as a FIELD. A rejected save (409 — somebody else holds the lock,
            // nothing was written) must be distinguishable from a server error,
            // and a status buried in a message string cannot carry that.
            // The message text is deliberately unchanged: existing callers parse
            // it (e.g. `error.message.includes('status 404')`) and keep working.
            const serverError = await BackendClient.readServerError(response);
            throw new HttpError(
                `request ${method.toUpperCase()} ${uri} failed with status ${response.status.toString()} (${response.statusText})`,
                response.status,
                serverError.code,
                serverError.message,
            );
        }

        if (response.status === 204) {
            return null;
        }

        return ((await response.json()) as { data: T }).data;
    }

    /**
     * The backend's `{error: {code, message}}` of a failed response, when the body is that JSON;
     * empty for an empty, HTML or otherwise unreadable body (nginx error pages, a 500 without body).
     */
    private static async readServerError(response: Response): Promise<{code?: number; message?: string}> {
        try {
            const body = (await response.json()) as {error?: {code?: unknown; message?: unknown}};
            const code = body?.error?.code;
            const message = body?.error?.message;

            return {
                code: typeof code === 'number' ? code : undefined,
                message: typeof message === 'string' ? message : undefined,
            };
        } catch {
            return {};
        }
    }
}
